import { create } from "zustand";
import { HOURS_PER_YEAR } from "@/lib/constants";
import { emit } from "@/lib/event-bus";
import { evalTimeBounds, nextExpiryDTE } from "@/lib/market/hours";
import { currentETHour } from "@/lib/market/time";
import {
  EXPIRY_ANCHOR_MS_PARAM,
  layerExpiryDtes,
} from "@/lib/options/leg-premium";
import { layerColor } from "@/lib/palette";
import { latestPriceFor } from "@/lib/quotes";
import { strategyChoiceKey } from "@/lib/strategy/choice-key";
import { getStrategyColorDefault } from "@/lib/strategy/color-defaults";
import {
  ANCHOR_LOCKED_PARAM,
  preserveAnchorAcrossEntrySpot,
  setAnchorLocked,
  setLegDelta,
  setLegOff,
} from "@/lib/strategy/leg-deltas";
import { getRegistryEntry } from "@/lib/strategy/registry";
import {
  increasesStrategySlotOverflow,
  MAX_SIMULATED_STRATEGIES_PER_EXPIRY,
  simulationCapacityOverflowLayers,
  strategySlotCapacityOverflowLayers,
} from "@/lib/strategy/simulation-capacity";
import {
  layerSimulationExpiryDtes,
  simulationScopeLayers,
} from "@/lib/strategy/simulation-scope";
import { DEFAULT_TICKER, getStrikeStep } from "@/lib/tickers/registry";
import type { StrategyLayer, StrategyLayerKind } from "@/types/strategy";
import { useLiveQuotesStore } from "@/stores/liveQuotesStore";

export interface PreviewLayer {
  kind: StrategyLayerKind;
  color: string;
  params?: Record<string, number>;
}

export type FocusedStructureTarget =
  | { type: "layer"; layerId: string }
  | { type: "leg"; layerId: string; index: number };

const MS_PER_YEAR = HOURS_PER_YEAR * 3600_000;
export const DEFAULT_PAYOFF_ZOOM_RANGE = 200;
export { MAX_SIMULATED_STRATEGIES_PER_EXPIRY };

// SpotInput bus channels — the keys carried by `spotinput:set`.
const SPOT_KEY = "evalSpot";
const IV_KEY = "iv";

function getLegCount(
  kind: StrategyLayerKind,
  params: Record<string, number>,
): number {
  if (params.leg_count != null) return params.leg_count;
  return getRegistryEntry(kind).toLegs(0, params).length;
}

function anyLegOn(
  kind: StrategyLayerKind,
  params: Record<string, number>,
): boolean {
  const legCount = getLegCount(kind, params);
  for (let i = 0; i < legCount; i++) {
    if (params[`leg_off_${i}`] !== 1) return true;
  }
  return false;
}

function maxExpiryDte(layers: StrategyLayer[], fallbackDte: number): number {
  return layers.reduce(
    (max, layer) => {
      const dtes = layerExpiryDtes(layer, fallbackDte);
      return dtes.reduce((nextMax, dte) => Math.max(nextMax, dte), max);
    },
    layers.length > 0 ? 0 : fallbackDte,
  );
}

function maxSimulationExpiryDte(
  layers: StrategyLayer[],
  fallbackDte: number,
  holidays: Set<string>,
): number {
  return layers.reduce(
    (max, layer) => {
      const dtes = layerSimulationExpiryDtes(layer, fallbackDte, holidays);
      return dtes.reduce((nextMax, dte) => Math.max(nextMax, dte), max);
    },
    layers.length > 0 ? 0 : fallbackDte,
  );
}

function expirySelectionAfterLayerEdit(
  state: SimulatorState,
  layers: StrategyLayer[],
  layerId: string,
): number | null {
  const selected = state.selectedExpiryDte;
  if (
    selected === null ||
    (state.selectedLayerTab !== null && state.selectedLayerTab !== layerId)
  ) {
    return selected;
  }
  const previous = state.layers.find((layer) => layer.id === layerId);
  const updated = layers.find((layer) => layer.id === layerId);
  if (!previous || !updated) return selected;
  const previousDtes = layerSimulationExpiryDtes(
    previous,
    state.dte,
    state.holidays,
  );
  const updatedDtes = layerSimulationExpiryDtes(
    updated,
    state.dte,
    state.holidays,
  );
  // Follow the edited strategy when it leaves the active date, even if other
  // strategies remain there. Keeping both changes atomic preserves its editor.
  if (!previousDtes.includes(selected) || updatedDtes.includes(selected)) {
    return selected;
  }
  return (
    updatedDtes.find((dte) => !previousDtes.includes(dte)) ??
    updatedDtes[0] ??
    selected
  );
}

function moveLayerExpiry(layer: StrategyLayer, nextDte: number): StrategyLayer {
  const normalized = Math.max(0, Math.round(nextDte));
  const expiryAnchorMs = Date.now();
  const frontDte = Number(layer.params.frontDTE);
  const backDte = Number(layer.params.backDTE);

  if (Number.isFinite(frontDte) && Number.isFinite(backDte)) {
    const spread = Math.max(1, Math.round(backDte - frontDte));
    const { expiryDTE: _expiryDTE, ...params } = layer.params;
    return {
      ...layer,
      params: {
        ...params,
        [EXPIRY_ANCHOR_MS_PARAM]: expiryAnchorMs,
        frontDTE: normalized,
        backDTE: normalized + spread,
      },
    };
  }

  return {
    ...layer,
    params: {
      ...layer.params,
      [EXPIRY_ANCHOR_MS_PARAM]: expiryAnchorMs,
      expiryDTE: normalized,
    },
  };
}

function layersForEvalRange(
  layers: StrategyLayer[],
  selectedLayerTab: string | null,
  selectedExpiryDte: number | null,
  fallbackDte: number,
  holidays: Set<string>,
): StrategyLayer[] {
  const scoped = simulationScopeLayers(
    layers,
    selectedLayerTab,
    selectedExpiryDte,
    fallbackDte,
    false,
    holidays,
  );
  return scoped.length > 0 ? scoped : layers;
}

function evalBoundsForLayers(
  dte: number,
  layers: StrategyLayer[],
  holidays: Set<string>,
  date: Date = new Date(),
  selectedLayerTab: string | null = null,
  selectedExpiryDte: number | null = null,
): { rangeStartMs: number; rangeEndMs: number } {
  return evalTimeBounds(
    maxSimulationExpiryDte(
      layersForEvalRange(
        layers,
        selectedLayerTab,
        selectedExpiryDte,
        dte,
        holidays,
      ),
      dte,
      holidays,
    ),
    holidays,
    date,
  );
}

function preserveUnpinnedLayerAnchors(
  layers: StrategyLayer[],
  previousEntrySpot: number | null,
  nextEntrySpot: number | null,
): StrategyLayer[] {
  if (previousEntrySpot == null || nextEntrySpot == null) return layers;
  return layers.map((layer) => ({
    ...layer,
    params: preserveAnchorAcrossEntrySpot(
      layer.params,
      previousEntrySpot,
      nextEntrySpot,
    ),
  }));
}

export function syncLayerEnabled(
  layer: StrategyLayer,
  enabled: boolean,
): StrategyLayer {
  const legCount = getLegCount(layer.kind, layer.params);
  let params = layer.params;
  for (let i = 0; i < legCount; i++) {
    params = setLegOff(params, i, !enabled);
  }
  return { ...layer, enabled, params };
}

function capacityAllowsLayers(
  layers: StrategyLayer[],
  fallbackDte: number,
  holidays: Set<string>,
  previousLayers?: readonly StrategyLayer[],
): boolean {
  if (
    previousLayers &&
    increasesStrategySlotOverflow(previousLayers, layers, fallbackDte, holidays)
  ) {
    const overflow = strategySlotCapacityOverflowLayers(
      layers,
      fallbackDte,
      holidays,
    );
    emit("strategy:limit-reached", {
      layers: overflow,
      resolution: "remove",
    });
    return false;
  }

  const overflow = simulationCapacityOverflowLayers(
    layers,
    fallbackDte,
    holidays,
  );
  if (overflow.length === 0) return true;
  emit("strategy:limit-reached", { layers: overflow });
  return false;
}

export interface ScenarioEphemeral {
  entrySpot: number | null;
  entryIV: number | null;
  entryCursorMs: number;
  evalSpot: number | null;
  iv: number | null;
  entryTime: number;
  dte: number;
}

export function initEphemeral(
  liveSpot: number | null,
  liveIV: number | null,
  holidays: Set<string>,
): ScenarioEphemeral {
  const now = new Date();
  const dte = nextExpiryDTE(now, holidays);
  const { rangeStartMs } = evalTimeBounds(dte, holidays, now);
  return {
    entrySpot: liveSpot,
    entryIV: liveIV,
    entryCursorMs: rangeStartMs,
    evalSpot: liveSpot,
    iv: liveIV,
    entryTime: currentETHour(),
    dte,
  };
}

interface SimulatorState {
  // ── External inputs (seeded by the provider) ──
  holidays: Set<string>;

  // ── Active ticker ──
  // Looked up at compute time for strike-grid snapping. Set by `loadTab` from
  // the active template's `ticker` column. Engine code never reads ticker
  // properties off layer params — this is the seam.
  activeTicker: string;

  // ── Scenario ──
  entrySpot: number | null;
  entryIV: number | null;
  entryCursorMs: number;
  evalSpot: number | null;
  scenarioIV: number | null;
  dte: number;
  entryTime: number;
  evalRangeStartMs: number;
  evalRangeEndMs: number;
  evalCursorMs: number;
  evalCursorPinned: boolean;
  userEditedMarket: boolean;
  inFlightReset: boolean;
  pendingScrolls: Set<string>;

  // ── Layers ──
  layers: StrategyLayer[];
  nextLayerSeq: number;
  selectedLayerTab: string | null;
  selectedExpiryDte: number | null;
  focusedStructureTarget: FocusedStructureTarget | null;

  // ── Simulator UI sync ──
  simMode: "preview" | "payoff" | "decay" | "manifold";
  editingLayers: boolean;
  previewLayers: PreviewLayer[];

  // ── Payoff chart zoom ──
  payoffZoomRange: number;
  payoffZoomCenter: number | null;
  payoffLocked: boolean;
  payoffHoverSpot: number | null;
  decayHoverCursorMs: number | null;
  expiryDateSelectionUnlocked: boolean;

  // ── Tab switch ──
  loadedTabId: string | null;
  loadedWorkspaceId: string | null;
  isSwitchingTab: boolean;
  layerCache: Map<string, StrategyLayer[]>;

  // ── Ticker actions ──
  setActiveTicker: (ticker: string) => void;
  switchTicker: (ticker: string) => void;

  // ── Scenario actions ──
  setHolidays: (holidays: Set<string>) => void;
  seedScenario: (args: {
    initialSpot: number | null;
    initialIV: number | null;
    holidays: Set<string>;
  }) => void;
  setEntrySpotRaw: (v: number | null) => void;
  setEvalSpotRaw: (v: number | null) => void;
  syncLiveSpot: (liveSpot: number) => void;
  syncLiveIV: (liveIV: number) => void;
  setScenarioIV: (v: number | null) => void;
  setEntryTimeRaw: (v: number) => void;
  tickLiveClock: (now?: Date) => void;
  setDTE: (v: number) => void;
  setEvalCursorMs: (ms: number) => void;
  setEntrySpot: (v: number) => void;
  setEvalSpot: (v: number) => void;
  setSpot: (v: number) => void;
  setIV: (v: number) => void;
  resetEphemeral: (next: ScenarioEphemeral) => void;
  resetToLive: (liveIV?: number | null) => void;
  registerScroll: (id: string) => void;
  unregisterScroll: (id: string) => void;
  unpin: () => void;

  // ── Layer actions ──
  setLayers: (
    updater: StrategyLayer[] | ((prev: StrategyLayer[]) => StrategyLayer[]),
  ) => void;
  addLayer: (
    kind: StrategyLayerKind,
    label?: string,
    color?: string,
    params?: Record<string, number>,
  ) => string | null;
  duplicateLayer: (id: string) => string | null;
  removeLayer: (id: string) => void;
  toggleLayer: (id: string) => void;
  selectLayerTab: (id: string | null) => void;
  selectExpiryDte: (dte: number | null) => void;
  setFocusedStructureTarget: (target: FocusedStructureTarget | null) => void;
  setExpiryForLayerScope: (dte: number, layerId: string | null) => void;
  updateLayerParam: (id: string, key: string, value: number) => void;
  updateLayerPivotFromPayoff: (id: string, value: number) => void;
  updateLayer: (
    id: string,
    updates: Partial<Pick<StrategyLayer, "label" | "color" | "kind">>,
  ) => void;
  restoreLayer: (id: string, saved: StrategyLayer) => void;
  reorderLayers: (fromIndex: number, toIndex: number) => void;
  loadLayers: (layers: StrategyLayer[]) => void;

  // ── UI sync actions ──
  setSimMode: (mode: "preview" | "payoff" | "decay" | "manifold") => void;
  setEditingLayers: (editing: boolean) => void;
  setPreviewLayers: (previewLayers: PreviewLayer[]) => void;
  setPayoffZoom: (args: { range: number; center: number | null }) => void;
  setPayoffHoverSpot: (spot: number | null) => void;
  setDecayHoverCursorMs: (ms: number | null) => void;
  resetPayoffZoom: () => void;
  togglePayoffLocked: () => void;
  setExpiryDateSelectionUnlocked: (unlocked: boolean) => void;
  toggleExpiryDateSelectionUnlocked: () => void;

  // ── Tab-switch actions ──
  loadTab: (args: {
    tabId: string;
    templateLayers: StrategyLayer[];
    liveSpot: number | null;
    liveIV: number | null;
    ticker?: string;
  }) => void;
  loadWorkspace: (args: {
    workspaceId: string;
    workspaceLayers: StrategyLayer[];
    workspaceState: Record<string, unknown>;
    liveSpot: number | null;
    liveIV: number | null;
    ticker: string;
    force?: boolean;
  }) => void;
  clearWorkspace: () => void;
  ackTabSwitch: () => void;
  setCachedLayers: (tabId: string, layers: StrategyLayer[]) => void;
  clearCachedLayers: (tabId: string) => void;
}

const EMPTY_HOLIDAYS = new Set<string>();
const INITIAL_BOUNDS = evalTimeBounds(0, EMPTY_HOLIDAYS, new Date(0));
const PAYOFF_HOVER_SPOT_EPSILON = 0.01;

export const useSimulatorStore = create<SimulatorState>((set, get) => ({
  holidays: EMPTY_HOLIDAYS,
  activeTicker: DEFAULT_TICKER,

  entrySpot: null,
  entryIV: null,
  entryCursorMs: 0,
  evalSpot: null,
  scenarioIV: null,
  dte: 0,
  entryTime: 0,
  evalRangeStartMs: INITIAL_BOUNDS.rangeStartMs,
  evalRangeEndMs: INITIAL_BOUNDS.rangeEndMs,
  evalCursorMs: INITIAL_BOUNDS.rangeStartMs,
  evalCursorPinned: false,
  userEditedMarket: false,
  inFlightReset: false,
  pendingScrolls: new Set<string>(),

  layers: [],
  nextLayerSeq: 0,
  selectedLayerTab: null,
  selectedExpiryDte: null,
  focusedStructureTarget: null,

  loadedTabId: null,
  loadedWorkspaceId: null,
  isSwitchingTab: false,
  layerCache: new Map(),

  simMode: "decay",
  editingLayers: false,
  previewLayers: [],

  payoffZoomRange: DEFAULT_PAYOFF_ZOOM_RANGE,
  payoffZoomCenter: null,
  payoffLocked: true,
  payoffHoverSpot: null,
  decayHoverCursorMs: null,
  expiryDateSelectionUnlocked: false,

  setActiveTicker: (activeTicker) => set({ activeTicker }),

  switchTicker: (next) => {
    if (next === get().activeTicker) return;
    const livePrice = latestPriceFor(useLiveQuotesStore.getState(), next);
    set({
      activeTicker: next,
      ...(livePrice !== null
        ? { entrySpot: livePrice, evalSpot: livePrice }
        : null),
      userEditedMarket: false,
      inFlightReset: false,
      pendingScrolls: new Set(),
    });
    if (livePrice !== null)
      emit("spotinput:set", { key: SPOT_KEY, value: livePrice });
  },

  setHolidays: (holidays) => set({ holidays }),

  seedScenario: ({ initialSpot, initialIV, holidays }) => {
    const s = get();
    if (s.evalSpot !== null || s.scenarioIV !== null) {
      if (holidays !== s.holidays) {
        const { rangeStartMs, rangeEndMs } = evalBoundsForLayers(
          s.dte,
          s.layers,
          holidays,
          new Date(),
          s.selectedLayerTab,
          s.selectedExpiryDte,
        );
        set({
          holidays,
          evalRangeStartMs: rangeStartMs,
          evalRangeEndMs: rangeEndMs,
          evalCursorMs: s.evalCursorPinned
            ? Math.max(rangeStartMs, Math.min(s.evalCursorMs, rangeEndMs))
            : rangeStartMs,
        });
      }
      return;
    }
    const init = initEphemeral(initialSpot, initialIV, holidays);
    const { rangeStartMs, rangeEndMs } = evalBoundsForLayers(
      init.dte,
      [],
      holidays,
      new Date(),
    );
    set({
      holidays,
      entrySpot: init.entrySpot,
      entryIV: init.entryIV,
      entryCursorMs: init.entryCursorMs,
      evalSpot: init.evalSpot,
      scenarioIV: init.iv,
      dte: init.dte,
      entryTime: init.entryTime,
      evalRangeStartMs: rangeStartMs,
      evalRangeEndMs: rangeEndMs,
      evalCursorMs: rangeStartMs,
      evalCursorPinned: false,
    });
  },

  setEntrySpotRaw: (entrySpot) =>
    set((s) => ({
      entrySpot,
      layers: preserveUnpinnedLayerAnchors(s.layers, s.entrySpot, entrySpot),
    })),
  setEvalSpotRaw: (evalSpot) => set({ evalSpot }),
  syncLiveSpot: (liveSpot) => {
    const { entrySpot, layers } = get();
    const nextLayers = preserveUnpinnedLayerAnchors(
      layers,
      entrySpot,
      liveSpot,
    );
    // The store is the source of truth; the ruler animation only mirrors it.
    // Waiting for SpotInput to settle can drop the quote because programmatic
    // scrolls intentionally suppress their onChange callback.
    set({
      entrySpot: liveSpot,
      evalSpot: liveSpot,
      layers: nextLayers,
      inFlightReset: true,
    });
    emit("spotinput:set", { key: SPOT_KEY, value: liveSpot });
    if (get().pendingScrolls.size === 0) {
      set({ inFlightReset: false });
    }
  },
  syncLiveIV: (liveIV) => {
    set({ entryIV: liveIV, scenarioIV: liveIV, inFlightReset: true });
    emit("spotinput:set", { key: IV_KEY, value: liveIV * 100 });
    if (get().pendingScrolls.size === 0) {
      set({ inFlightReset: false });
    }
  },
  setScenarioIV: (scenarioIV) => set({ scenarioIV }),
  setEntryTimeRaw: (entryTime) => set({ entryTime }),
  tickLiveClock: (now = new Date()) => {
    const state = get();
    const { rangeStartMs, rangeEndMs } = evalBoundsForLayers(
      state.dte,
      state.layers,
      state.holidays,
      now,
      state.selectedLayerTab,
      state.selectedExpiryDte,
    );
    set({
      entryTime: currentETHour(),
      evalRangeStartMs: rangeStartMs,
      evalRangeEndMs: rangeEndMs,
      evalCursorMs: state.evalCursorPinned
        ? Math.max(rangeStartMs, Math.min(state.evalCursorMs, rangeEndMs))
        : rangeStartMs,
    });
  },

  setDTE: (v) => {
    const { holidays, layers, selectedLayerTab, selectedExpiryDte } = get();
    const { rangeStartMs, rangeEndMs } = evalBoundsForLayers(
      v,
      layers,
      holidays,
      new Date(),
      selectedLayerTab,
      selectedExpiryDte,
    );
    set({
      dte: v,
      evalRangeStartMs: rangeStartMs,
      evalRangeEndMs: rangeEndMs,
      evalCursorMs: rangeStartMs,
      evalCursorPinned: false,
    });
  },

  setEvalCursorMs: (ms) => {
    const { evalCursorMs, evalRangeStartMs, evalRangeEndMs, inFlightReset } =
      get();
    const clamped = Math.max(evalRangeStartMs, Math.min(ms, evalRangeEndMs));
    if (clamped === evalCursorMs) return;
    set(
      inFlightReset
        ? { evalCursorMs: clamped }
        : { evalCursorMs: clamped, evalCursorPinned: true },
    );
  },

  setEntrySpot: (v) => {
    const { inFlightReset } = get();
    set((s) => ({
      ...(inFlightReset
        ? { entrySpot: v }
        : { entrySpot: v, userEditedMarket: true }),
      layers: preserveUnpinnedLayerAnchors(s.layers, s.entrySpot, v),
    }));
  },
  setEvalSpot: (v) => {
    const { inFlightReset } = get();
    set(
      inFlightReset ? { evalSpot: v } : { evalSpot: v, userEditedMarket: true },
    );
    if (!inFlightReset) emit("spotinput:set", { key: SPOT_KEY, value: v });
  },
  setSpot: (v) => {
    const { inFlightReset } = get();
    set(
      inFlightReset ? { evalSpot: v } : { evalSpot: v, userEditedMarket: true },
    );
    if (!inFlightReset) emit("spotinput:set", { key: SPOT_KEY, value: v });
  },
  setIV: (v) => {
    const { inFlightReset } = get();
    set(
      inFlightReset
        ? { scenarioIV: v }
        : { scenarioIV: v, userEditedMarket: true },
    );
  },

  resetEphemeral: (next) => {
    const { holidays, layers, selectedLayerTab, selectedExpiryDte } = get();
    const { rangeStartMs, rangeEndMs } = evalBoundsForLayers(
      next.dte,
      layers,
      holidays,
      new Date(),
      selectedLayerTab,
      selectedExpiryDte,
    );
    set({
      entrySpot: next.entrySpot,
      entryIV: next.entryIV,
      entryCursorMs: next.entryCursorMs,
      evalSpot: next.evalSpot,
      scenarioIV: next.iv,
      entryTime: next.entryTime,
      dte: next.dte,
      evalRangeStartMs: rangeStartMs,
      evalRangeEndMs: rangeEndMs,
      evalCursorMs: rangeStartMs,
      evalCursorPinned: false,
      userEditedMarket: false,
      inFlightReset: false,
      pendingScrolls: new Set(),
      payoffZoomRange: DEFAULT_PAYOFF_ZOOM_RANGE,
      payoffZoomCenter: null,
      payoffHoverSpot: null,
    });
    if (next.evalSpot != null)
      emit("spotinput:set", { key: SPOT_KEY, value: next.evalSpot });
    if (next.iv != null)
      emit("spotinput:set", { key: IV_KEY, value: next.iv * 100 });
  },

  resetToLive: (resolvedLiveIV = null) => {
    const live = useLiveQuotesStore.getState();
    const {
      activeTicker,
      evalRangeStartMs,
      evalCursorMs,
      evalCursorPinned,
      entrySpot,
      layers,
    } = get();
    const spot = latestPriceFor(live, activeTicker);
    const iv = resolvedLiveIV;
    const anyReset =
      spot != null ||
      iv != null ||
      evalCursorPinned ||
      evalCursorMs !== evalRangeStartMs;
    const willResetCursor = evalCursorMs !== evalRangeStartMs;
    set({
      ...(spot != null ? { entrySpot: spot, evalSpot: spot } : null),
      ...(spot != null
        ? { layers: preserveUnpinnedLayerAnchors(layers, entrySpot, spot) }
        : null),
      ...(iv != null ? { entryIV: iv, scenarioIV: iv } : null),
      ...(willResetCursor ? { evalCursorMs: evalRangeStartMs } : null),
      evalCursorPinned: false,
      userEditedMarket: false,
      inFlightReset: anyReset,
    });
    if (spot != null) emit("spotinput:set", { key: SPOT_KEY, value: spot });
    if (iv != null) emit("spotinput:set", { key: IV_KEY, value: iv * 100 });
    if (willResetCursor) emit("scenario:cursor-reset");
    if (anyReset && get().pendingScrolls.size === 0) {
      set({ inFlightReset: false });
    }
  },

  registerScroll: (id) => {
    const { pendingScrolls } = get();
    if (pendingScrolls.has(id)) return;
    const next = new Set(pendingScrolls);
    next.add(id);
    set({ pendingScrolls: next });
  },
  unregisterScroll: (id) => {
    const { pendingScrolls, inFlightReset } = get();
    if (!pendingScrolls.has(id)) return;
    const next = new Set(pendingScrolls);
    next.delete(id);
    set({
      pendingScrolls: next,
      ...(next.size === 0 && inFlightReset
        ? { inFlightReset: false, userEditedMarket: false }
        : null),
    });
  },
  unpin: () => set({ userEditedMarket: true }),

  // ── Layer actions ──────────────────────────────────────────────────────────

  setLayers: (updater) =>
    set((s) => {
      const updatedLayers =
        typeof updater === "function" ? updater(s.layers) : updater;
      const layers = updatedLayers.map((layer) =>
        Number.isFinite(layer.params[EXPIRY_ANCHOR_MS_PARAM])
          ? layer
          : {
              ...layer,
              params: {
                ...layer.params,
                [EXPIRY_ANCHOR_MS_PARAM]: Date.now(),
              },
            },
      );
      if (!capacityAllowsLayers(layers, s.dte, s.holidays, s.layers)) return s;
      const { rangeStartMs, rangeEndMs } = evalBoundsForLayers(
        s.dte,
        layers,
        s.holidays,
        new Date(),
        s.selectedLayerTab,
        s.selectedExpiryDte,
      );
      return {
        layers,
        evalRangeStartMs: rangeStartMs,
        evalRangeEndMs: rangeEndMs,
        evalCursorMs: Math.max(
          rangeStartMs,
          Math.min(s.evalCursorMs, rangeEndMs),
        ),
      };
    }),

  addLayer: (kind, label, color, paramOverrides) => {
    const {
      entrySpot,
      layers,
      nextLayerSeq,
      selectedLayerTab,
      selectedExpiryDte,
      activeTicker,
      dte,
      holidays,
      evalCursorMs,
      evalCursorPinned,
    } = get();
    const now = new Date();
    const nowMs = now.getTime();
    const entry = getRegistryEntry(kind);
    const spot = entrySpot ?? 0;
    const strikeStep = getStrikeStep(activeTicker);
    const id = `layer-${Date.now()}-${nextLayerSeq}`;
    const params: Record<string, number> = {
      ...entry.defaultParams(spot, strikeStep),
      ...paramOverrides,
      [EXPIRY_ANCHOR_MS_PARAM]: nowMs,
    };
    const defaultColor = getStrategyColorDefault(
      strategyChoiceKey(kind, params),
    );
    const layerDte = Number.isFinite(params.expiryDTE)
      ? Number(params.expiryDTE)
      : dte;
    const nextDte = Math.max(dte, layerDte);
    const baseLayer: StrategyLayer = {
      id,
      kind,
      params,
      color: color ?? defaultColor ?? layerColor(layers.length),
      enabled: true,
      ...(label ? { label } : {}),
    };
    if (
      increasesStrategySlotOverflow(
        layers,
        [...layers, baseLayer],
        dte,
        holidays,
      )
    ) {
      emit("strategy:limit-reached", {
        layers: [baseLayer],
        resolution: "remove",
      });
      return null;
    }
    const newLayer = baseLayer;
    const selectedAfterAdd = selectedLayerTab !== null ? id : selectedLayerTab;
    const [nearestAddedExpiry] = layerSimulationExpiryDtes(
      newLayer,
      dte,
      holidays,
    ).sort((left, right) => left - right);
    const selectedExpiryAfterAdd =
      selectedAfterAdd === null
        ? (nearestAddedExpiry ?? selectedExpiryDte)
        : null;
    const { rangeStartMs, rangeEndMs } = evalBoundsForLayers(
      nextDte,
      [...layers, newLayer],
      holidays,
      now,
      selectedAfterAdd,
      selectedExpiryAfterAdd,
    );
    set({
      nextLayerSeq: nextLayerSeq + 1,
      dte: nextDte,
      evalRangeStartMs: rangeStartMs,
      evalRangeEndMs: rangeEndMs,
      evalCursorMs: evalCursorPinned
        ? Math.max(rangeStartMs, Math.min(evalCursorMs, rangeEndMs))
        : rangeStartMs,
      layers: [...layers, newLayer],
      selectedLayerTab: selectedAfterAdd,
      selectedExpiryDte: selectedExpiryAfterAdd,
    });
    return id;
  },

  duplicateLayer: (id) => {
    const { layers, nextLayerSeq, dte, holidays } = get();
    const source = layers.find((layer) => layer.id === id);
    if (!source) return null;

    const groupId = source.duplicateGroupId ?? source.id;
    const groupLayers = layers.filter(
      (layer) => layer.id === groupId || layer.duplicateGroupId === groupId,
    );
    const duplicateIndex =
      Math.max(1, ...groupLayers.map((layer) => layer.duplicateIndex ?? 1)) + 1;
    const duplicateId = `layer-${Date.now()}-${nextLayerSeq}`;
    const baseDuplicate: StrategyLayer = {
      ...source,
      id: duplicateId,
      params: { ...source.params },
      duplicateGroupId: groupId,
      duplicateIndex,
    };
    if (
      increasesStrategySlotOverflow(
        layers,
        [...layers, baseDuplicate],
        dte,
        holidays,
      )
    ) {
      emit("strategy:limit-reached", {
        layers: [baseDuplicate],
        resolution: "remove",
      });
      return null;
    }
    const duplicate = baseDuplicate;
    const lastGroupIndex = layers.reduce(
      (lastIndex, layer, index) =>
        layer.id === groupId || layer.duplicateGroupId === groupId
          ? index
          : lastIndex,
      layers.indexOf(source),
    );
    const nextLayers = [...layers];
    nextLayers.splice(lastGroupIndex + 1, 0, duplicate);
    set({
      layers: nextLayers,
      nextLayerSeq: nextLayerSeq + 1,
      selectedLayerTab: null,
    });
    return duplicateId;
  },

  removeLayer: (id) =>
    set((s) => {
      const target = s.layers.find((l) => l.id === id);
      if (!target) return s;
      const wasSelected = s.selectedLayerTab === id;
      const remaining = s.layers.filter((l) => l.id !== id);
      const selectedLayerTab =
        remaining.length === 0 || wasSelected ? null : s.selectedLayerTab;
      const fallbackDte = nextExpiryDTE(new Date(), s.holidays);
      const remainingMaxDte =
        remaining.length > 0
          ? maxExpiryDte(remaining, fallbackDte)
          : fallbackDte;
      const dte = Math.min(s.dte, remainingMaxDte);
      const { rangeStartMs, rangeEndMs } = evalBoundsForLayers(
        dte,
        remaining,
        s.holidays,
        new Date(),
        selectedLayerTab,
        s.selectedExpiryDte,
      );
      return {
        layers: remaining,
        dte,
        selectedLayerTab,
        evalRangeStartMs: rangeStartMs,
        evalRangeEndMs: rangeEndMs,
        evalCursorMs: Math.max(
          rangeStartMs,
          Math.min(s.evalCursorMs, rangeEndMs),
        ),
      };
    }),

  toggleLayer: (id) =>
    set((s) => {
      const layers = s.layers.map((l) =>
        l.id === id ? syncLayerEnabled(l, !l.enabled) : l,
      );
      if (!capacityAllowsLayers(layers, s.dte, s.holidays)) return s;
      // Visibility does not move the simulation window or its cursor.
      return { layers };
    }),

  selectLayerTab: (id) =>
    set((s) => {
      const selectedLayer = id
        ? s.layers.find((layer) => layer.id === id)
        : null;
      const selectedLayerTab = selectedLayer ? selectedLayer.id : null;
      const selectedLayerExpiryDtes = selectedLayer
        ? layerSimulationExpiryDtes(selectedLayer, s.dte, s.holidays)
        : [];
      const selectedExpiryDte =
        selectedLayer &&
        s.selectedExpiryDte !== null &&
        !selectedLayerExpiryDtes.includes(Math.round(s.selectedExpiryDte))
          ? (selectedLayerExpiryDtes[0] ?? s.selectedExpiryDte)
          : s.selectedExpiryDte;
      const rangeAnchor =
        Number.isFinite(s.evalRangeStartMs) && s.evalRangeStartMs > 0
          ? new Date(s.evalRangeStartMs)
          : new Date();
      const { rangeStartMs, rangeEndMs } = evalBoundsForLayers(
        s.dte,
        s.layers,
        s.holidays,
        rangeAnchor,
        selectedLayerTab,
        selectedExpiryDte,
      );
      return {
        selectedLayerTab,
        selectedExpiryDte,
        focusedStructureTarget: selectedLayerTab
          ? { type: "layer", layerId: selectedLayerTab }
          : null,
        payoffLocked: true,
        payoffHoverSpot: null,
        evalRangeStartMs: rangeStartMs,
        evalRangeEndMs: rangeEndMs,
        evalCursorMs: Math.max(
          rangeStartMs,
          Math.min(s.evalCursorMs, rangeEndMs),
        ),
      };
    }),

  selectExpiryDte: (selectedExpiryDte) =>
    set((s) => {
      const rangeAnchor =
        Number.isFinite(s.evalRangeStartMs) && s.evalRangeStartMs > 0
          ? new Date(s.evalRangeStartMs)
          : new Date();
      const { rangeStartMs, rangeEndMs } = evalBoundsForLayers(
        s.dte,
        s.layers,
        s.holidays,
        rangeAnchor,
        null,
        selectedExpiryDte,
      );
      return {
        selectedExpiryDte,
        selectedLayerTab: null,
        focusedStructureTarget: null,
        payoffLocked: true,
        payoffHoverSpot: null,
        evalRangeStartMs: rangeStartMs,
        evalRangeEndMs: rangeEndMs,
        evalCursorMs: Math.max(
          rangeStartMs,
          Math.min(s.evalCursorMs, rangeEndMs),
        ),
      };
    }),

  setFocusedStructureTarget: (focusedStructureTarget) =>
    set({ focusedStructureTarget }),

  setExpiryForLayerScope: (nextDte, layerId) =>
    set((s) => {
      const normalized = Math.max(0, Math.round(nextDte));
      const layers = s.layers.map((layer) =>
        layerId === null || layer.id === layerId
          ? moveLayerExpiry(layer, normalized)
          : layer,
      );
      if (!capacityAllowsLayers(layers, normalized, s.holidays, s.layers))
        return s;
      const { rangeStartMs, rangeEndMs } = evalBoundsForLayers(
        normalized,
        layers,
        s.holidays,
        new Date(),
        s.selectedLayerTab,
        s.selectedExpiryDte,
      );
      return {
        dte: normalized,
        layers,
        evalRangeStartMs: rangeStartMs,
        evalRangeEndMs: rangeEndMs,
        evalCursorMs: rangeStartMs,
      };
    }),

  updateLayerParam: (id, key, value) => {
    const deltaMatch = /^d_leg_(\d+)$/.exec(key);
    const legOffMatch = /^leg_off_(\d+)$/.exec(key);
    const premiumMatch = /^premium_leg_(\d+)$/.exec(key);
    const quantityMatch = /^q_leg_(\d+)$/.exec(key);
    const expiryParam =
      key === "expiryDTE" ||
      key === "frontDTE" ||
      key === "backDTE" ||
      /^expiry_leg_\d+$/.test(key);
    set((s) => {
      const layers = s.layers.map((l) => {
        if (l.id !== id) return l;
        if (deltaMatch)
          return {
            ...l,
            params: setLegDelta(l.params, Number(deltaMatch[1]), value),
          };
        if (legOffMatch) {
          const params = setLegOff(
            l.params,
            Number(legOffMatch[1]),
            value === 1,
          );
          const enabled = anyLegOn(l.kind, params);
          return { ...l, params, enabled };
        }
        if (premiumMatch) {
          if (!Number.isFinite(value)) {
            const { [key]: _premium, ...rest } = l.params;
            return { ...l, params: rest };
          }
          return {
            ...l,
            params: { ...l.params, [key]: Math.max(0, value) },
          };
        }
        if (quantityMatch) {
          const quantity = Math.max(1, Math.round(value));
          const { contracts: _contracts, ...withoutContracts } = l.params;
          if (quantity === 1) {
            const { [key]: _quantity, ...rest } = withoutContracts;
            return { ...l, params: rest };
          }
          return { ...l, params: { ...withoutContracts, [key]: quantity } };
        }
        if (key === ANCHOR_LOCKED_PARAM) {
          return { ...l, params: setAnchorLocked(l.params, value === 1) };
        }
        if (key === "pivot" && value === 0) {
          const { pivot: _pivot, ...rest } = l.params;
          return { ...l, params: rest };
        }
        if (key === "pivot") {
          return {
            ...l,
            params: { ...l.params, pivot: value },
          };
        }
        if (key === "contracts") {
          const contracts = Math.max(1, Math.round(value));
          if (contracts === 1) {
            const { contracts: _contracts, ...rest } = l.params;
            return { ...l, params: rest };
          }
          return { ...l, params: { ...l.params, contracts } };
        }
        if (key === "expiryDTE" && l.kind === "custom") {
          // A strategy-level expiry edit applies to the whole combination,
          // including imported legs that carry explicit expiry overrides.
          const params = { ...l.params };
          for (const paramKey of Object.keys(params)) {
            if (/^expiry_leg_\d+$/.test(paramKey)) params[paramKey] = value;
          }
          return {
            ...l,
            params: {
              ...params,
              [EXPIRY_ANCHOR_MS_PARAM]: Date.now(),
              expiryDTE: value,
            },
          };
        }
        return {
          ...l,
          params: {
            ...l.params,
            ...(expiryParam ? { [EXPIRY_ANCHOR_MS_PARAM]: Date.now() } : null),
            [key]: value,
          },
        };
      });
      if (!capacityAllowsLayers(layers, s.dte, s.holidays, s.layers)) return s;
      if (legOffMatch) return { layers };
      const selectedExpiryDte = expiryParam
        ? expirySelectionAfterLayerEdit(s, layers, id)
        : s.selectedExpiryDte;
      const { rangeStartMs, rangeEndMs } = evalBoundsForLayers(
        s.dte,
        layers,
        s.holidays,
        new Date(),
        s.selectedLayerTab,
        selectedExpiryDte,
      );
      return {
        layers,
        selectedExpiryDte,
        evalRangeStartMs: rangeStartMs,
        evalRangeEndMs: rangeEndMs,
        payoffHoverSpot: premiumMatch ? null : s.payoffHoverSpot,
        evalCursorMs: Math.max(
          rangeStartMs,
          Math.min(s.evalCursorMs, rangeEndMs),
        ),
      };
    });
  },

  updateLayerPivotFromPayoff: (id, value) => {
    set((s) => {
      const target = s.layers.find((layer) => layer.id === id);
      if (!target || (target.params.pivot ?? 0) === value) return s;
      return {
        layers: s.layers.map((layer) => {
          if (layer.id !== id) return layer;
          if (value === 0) {
            const { pivot: _pivot, ...rest } = layer.params;
            return { ...layer, params: rest };
          }
          return { ...layer, params: { ...layer.params, pivot: value } };
        }),
      };
    });
  },

  updateLayer: (id, updates) => {
    const { entrySpot, activeTicker } = get();
    const strikeStep = getStrikeStep(activeTicker);
    set((s) => {
      const layers = s.layers.map((l) => {
        if (l.id !== id) return l;
        const patched = { ...l, ...updates };
        if (patched.label === "") patched.label = undefined;
        if (updates.kind && updates.kind !== l.kind) {
          const entry = getRegistryEntry(updates.kind);
          patched.params = {
            ...entry.defaultParams(entrySpot ?? 0, strikeStep),
            [EXPIRY_ANCHOR_MS_PARAM]: Date.now(),
          };
        }
        return patched;
      });
      if (!capacityAllowsLayers(layers, s.dte, s.holidays, s.layers)) return s;
      const { rangeStartMs, rangeEndMs } = evalBoundsForLayers(
        s.dte,
        layers,
        s.holidays,
        new Date(),
        s.selectedLayerTab,
        s.selectedExpiryDte,
      );
      return {
        layers,
        evalRangeStartMs: rangeStartMs,
        evalRangeEndMs: rangeEndMs,
        evalCursorMs: Math.max(
          rangeStartMs,
          Math.min(s.evalCursorMs, rangeEndMs),
        ),
      };
    });
  },

  restoreLayer: (id, saved) =>
    set((s) => {
      const layers = s.layers.map((l) => (l.id === id ? { ...saved } : l));
      if (!capacityAllowsLayers(layers, s.dte, s.holidays, s.layers)) return s;
      const selectedExpiryDte = expirySelectionAfterLayerEdit(s, layers, id);
      const { rangeStartMs, rangeEndMs } = evalBoundsForLayers(
        s.dte,
        layers,
        s.holidays,
        new Date(),
        s.selectedLayerTab,
        selectedExpiryDte,
      );
      return {
        layers,
        selectedExpiryDte,
        evalRangeStartMs: rangeStartMs,
        evalRangeEndMs: rangeEndMs,
        evalCursorMs: Math.max(
          rangeStartMs,
          Math.min(s.evalCursorMs, rangeEndMs),
        ),
      };
    }),

  reorderLayers: (fromIndex, toIndex) =>
    set((s) => {
      const next = [...s.layers];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return { layers: next };
    }),

  loadLayers: (layers) =>
    set((s) => {
      const anchoredLayers = layers.map((layer) =>
        Number.isFinite(layer.params[EXPIRY_ANCHOR_MS_PARAM])
          ? layer
          : {
              ...layer,
              params: {
                ...layer.params,
                [EXPIRY_ANCHOR_MS_PARAM]: Date.now(),
              },
            },
      );
      const selectedLayerTab =
        s.selectedLayerTab &&
        anchoredLayers.some((layer) => layer.id === s.selectedLayerTab)
          ? s.selectedLayerTab
          : null;
      const { rangeStartMs, rangeEndMs } = evalBoundsForLayers(
        s.dte,
        anchoredLayers,
        s.holidays,
        new Date(),
        selectedLayerTab,
        s.selectedExpiryDte,
      );
      return {
        layers: anchoredLayers,
        selectedLayerTab,
        evalRangeStartMs: rangeStartMs,
        evalRangeEndMs: rangeEndMs,
        evalCursorMs: Math.max(
          rangeStartMs,
          Math.min(s.evalCursorMs, rangeEndMs),
        ),
      };
    }),

  // ── UI sync actions ───────────────────────────────────────────────────────

  setSimMode: (simMode) => set({ simMode, decayHoverCursorMs: null }),
  setEditingLayers: (editingLayers) => set({ editingLayers }),
  setPreviewLayers: (previewLayers) => set({ previewLayers }),
  setPayoffZoom: ({ range, center }) =>
    set({ payoffZoomRange: range, payoffZoomCenter: center }),
  setPayoffHoverSpot: (payoffHoverSpot) =>
    set((s) => {
      if (s.payoffHoverSpot == null && payoffHoverSpot == null) return s;
      if (
        s.payoffHoverSpot != null &&
        payoffHoverSpot != null &&
        Math.abs(s.payoffHoverSpot - payoffHoverSpot) <
          PAYOFF_HOVER_SPOT_EPSILON
      ) {
        return s;
      }
      return { payoffHoverSpot };
    }),
  setDecayHoverCursorMs: (decayHoverCursorMs) =>
    set((state) =>
      state.decayHoverCursorMs === decayHoverCursorMs
        ? state
        : { decayHoverCursorMs },
    ),
  resetPayoffZoom: () =>
    set({
      payoffZoomRange: DEFAULT_PAYOFF_ZOOM_RANGE,
      payoffZoomCenter: null,
      payoffHoverSpot: null,
    }),
  togglePayoffLocked: () =>
    set((s) => {
      const next = !s.payoffLocked;
      return { payoffLocked: next };
    }),
  setExpiryDateSelectionUnlocked: (expiryDateSelectionUnlocked) =>
    set({ expiryDateSelectionUnlocked }),
  toggleExpiryDateSelectionUnlocked: () =>
    set((s) => ({
      expiryDateSelectionUnlocked: !s.expiryDateSelectionUnlocked,
    })),

  // ── Tab-switch actions ────────────────────────────────────────────────────

  loadTab: ({ tabId, templateLayers, liveSpot, liveIV, ticker }) => {
    const s = get();
    const cached = s.layerCache.get(tabId);
    const nextLayers = cached ?? templateLayers;
    const alreadyLoaded =
      tabId === s.loadedTabId &&
      s.layers.length === nextLayers.length &&
      s.layers.every((layer, index) => layer.id === nextLayers[index]?.id);
    if (alreadyLoaded) return;
    const init = initEphemeral(liveSpot, liveIV, s.holidays);
    const { rangeStartMs, rangeEndMs } = evalBoundsForLayers(
      init.dte,
      nextLayers,
      s.holidays,
      new Date(),
    );
    const nextTicker = ticker ?? DEFAULT_TICKER;
    set({
      activeTicker: nextTicker,
      entrySpot: init.entrySpot,
      entryIV: init.entryIV,
      entryCursorMs: init.entryCursorMs,
      evalSpot: init.evalSpot,
      scenarioIV: init.iv,
      entryTime: init.entryTime,
      dte: init.dte,
      evalRangeStartMs: rangeStartMs,
      evalRangeEndMs: rangeEndMs,
      evalCursorMs: rangeStartMs,
      evalCursorPinned: false,
      userEditedMarket: false,
      inFlightReset: false,
      pendingScrolls: new Set(),
      layers: nextLayers,
      selectedLayerTab: null,
      selectedExpiryDte: null,
      focusedStructureTarget: null,
      payoffZoomRange: DEFAULT_PAYOFF_ZOOM_RANGE,
      payoffZoomCenter: null,
      payoffLocked: true,
      payoffHoverSpot: null,
      loadedTabId: tabId,
      loadedWorkspaceId: null,
      isSwitchingTab: true,
    });
    if (init.evalSpot != null)
      emit("spotinput:set", { key: SPOT_KEY, value: init.evalSpot });
    if (init.iv != null)
      emit("spotinput:set", { key: IV_KEY, value: init.iv * 100 });
  },

  loadWorkspace: ({
    workspaceId,
    workspaceLayers,
    workspaceState,
    liveSpot,
    liveIV,
    ticker,
    force = false,
  }) => {
    const s = get();
    // A workspace load is a document switch, not a synchronization primitive.
    // Scenario/view-state writes update the workspace object while local layer
    // edits may still be waiting to persist. Reloading the same workspace from
    // that temporarily stale object would erase those edits (most visibly,
    // a newly added strategy). Server-backed layer changes update the store
    // through their mutation path; only a different workspace id should
    // replace the complete simulator document here.
    if (!force && workspaceId === s.loadedWorkspaceId) {
      // A freshly imported workspace can be force-loaded before the resolved
      // market IV reaches this provider. The document effect then revisits the
      // same workspace with the real market inputs. Keep local document edits,
      // but fill/synchronize live inputs instead of discarding that update.
      if (!s.userEditedMarket) {
        if (
          liveSpot != null &&
          (s.entrySpot !== liveSpot || s.evalSpot !== liveSpot)
        ) {
          get().syncLiveSpot(liveSpot);
        }
        if (
          liveIV != null &&
          (s.entryIV !== liveIV || s.scenarioIV !== liveIV)
        ) {
          get().syncLiveIV(liveIV);
        }
      }
      return;
    }
    const init = initEphemeral(liveSpot, liveIV, s.holidays);
    const finite = (key: string, fallback: number): number => {
      const value = workspaceState[key];
      return typeof value === "number" && Number.isFinite(value)
        ? value
        : fallback;
    };
    const nullableFinite = (
      key: string,
      fallback: number | null,
    ): number | null => {
      const value = workspaceState[key];
      if (value === null) return null;
      return typeof value === "number" && Number.isFinite(value)
        ? value
        : fallback;
    };
    const savedMarketValue = (
      key: string,
      fallback: number | null,
    ): number | null => {
      const value = workspaceState[key];
      return typeof value === "number" && Number.isFinite(value)
        ? value
        : fallback;
    };
    const storedEntryCursorMs = finite("entryCursorMs", init.entryCursorMs);
    const nextWorkspaceLayers = workspaceLayers.map((layer) =>
      Number.isFinite(layer.params[EXPIRY_ANCHOR_MS_PARAM])
        ? layer
        : {
            ...layer,
            params: {
              ...layer.params,
              [EXPIRY_ANCHOR_MS_PARAM]: storedEntryCursorMs,
            },
          },
    );
    // Live workspaces must reopen from the current quote. Restoring a saved
    // spot after the live-sync effect has already run leaves the chart stuck
    // on that old value until the next quote change.
    const hasManualMarketOverride = workspaceState.userEditedMarket === true;
    const storedEntrySpot = hasManualMarketOverride
      ? savedMarketValue("entrySpot", init.entrySpot)
      : init.entrySpot;
    const storedEvalSpot = hasManualMarketOverride
      ? savedMarketValue("evalSpot", init.evalSpot)
      : init.evalSpot;
    const spotIsCompatible = (storedSpot: number | null): boolean => {
      if (
        storedSpot == null ||
        liveSpot == null ||
        storedSpot <= 0 ||
        liveSpot <= 0 ||
        !Number.isFinite(storedSpot) ||
        !Number.isFinite(liveSpot)
      ) {
        return true;
      }
      return (
        Math.max(storedSpot, liveSpot) / Math.min(storedSpot, liveSpot) <= 4
      );
    };
    const staleCrossTickerScenario =
      !spotIsCompatible(storedEntrySpot) || !spotIsCompatible(storedEvalSpot);
    const entrySpot = staleCrossTickerScenario
      ? init.entrySpot
      : storedEntrySpot;
    const evalSpot = staleCrossTickerScenario ? init.evalSpot : storedEvalSpot;
    const dte = Math.max(0, Math.round(finite("dte", init.dte)));
    const storedSelectedLayerTab =
      typeof workspaceState.selectedLayerTab === "string"
        ? workspaceState.selectedLayerTab
        : null;
    const selectedLayerTab =
      storedSelectedLayerTab &&
      nextWorkspaceLayers.some((layer) => layer.id === storedSelectedLayerTab)
        ? storedSelectedLayerTab
        : null;
    const storedSelectedExpiryDte = nullableFinite("selectedExpiryDte", null);
    // An empty expiry is a valid workspace filter; do not restore another date.
    const selectedExpiryDte =
      storedSelectedExpiryDte == null
        ? null
        : Math.max(0, Math.round(storedSelectedExpiryDte));
    const { rangeStartMs, rangeEndMs } = evalBoundsForLayers(
      dte,
      nextWorkspaceLayers,
      s.holidays,
      new Date(),
      selectedLayerTab,
      selectedExpiryDte,
    );
    const evalCursorMs = Math.max(
      rangeStartMs,
      Math.min(
        workspaceState.evalCursorPinned === true
          ? finite("evalCursorMs", rangeStartMs)
          : rangeStartMs,
        rangeEndMs,
      ),
    );
    const payoffZoomRange = Math.max(
      1,
      finite("payoffZoomRange", DEFAULT_PAYOFF_ZOOM_RANGE),
    );
    const entryIV = hasManualMarketOverride
      ? savedMarketValue("entryIV", init.entryIV)
      : init.entryIV;
    const scenarioIV = hasManualMarketOverride
      ? savedMarketValue("scenarioIV", init.iv)
      : init.iv;
    const simMode =
      workspaceState.simMode === "preview" ||
      workspaceState.simMode === "payoff" ||
      workspaceState.simMode === "decay" ||
      workspaceState.simMode === "manifold"
        ? workspaceState.simMode
        : s.simMode;

    set({
      activeTicker: ticker.toUpperCase(),
      entrySpot,
      entryIV,
      entryCursorMs: storedEntryCursorMs,
      evalSpot,
      scenarioIV,
      entryTime: finite("entryTime", init.entryTime),
      dte,
      evalRangeStartMs: rangeStartMs,
      evalRangeEndMs: rangeEndMs,
      evalCursorMs,
      evalCursorPinned: workspaceState.evalCursorPinned === true,
      userEditedMarket: !staleCrossTickerScenario && hasManualMarketOverride,
      inFlightReset: false,
      pendingScrolls: new Set(),
      layers: nextWorkspaceLayers,
      selectedLayerTab,
      selectedExpiryDte,
      focusedStructureTarget: null,
      payoffZoomRange,
      payoffZoomCenter: nullableFinite("payoffZoomCenter", null),
      payoffLocked: true,
      payoffHoverSpot: null,
      expiryDateSelectionUnlocked:
        workspaceState.expiryDateSelectionUnlocked === true,
      simMode,
      loadedTabId: null,
      loadedWorkspaceId: workspaceId,
      isSwitchingTab: true,
    });
    if (evalSpot != null)
      emit("spotinput:set", { key: SPOT_KEY, value: evalSpot });
    if (scenarioIV != null)
      emit("spotinput:set", { key: IV_KEY, value: scenarioIV * 100 });
  },

  clearWorkspace: () =>
    set({
      activeTicker: DEFAULT_TICKER,
      entrySpot: null,
      entryIV: null,
      entryCursorMs: 0,
      evalSpot: null,
      scenarioIV: null,
      dte: 0,
      entryTime: 0,
      evalRangeStartMs: INITIAL_BOUNDS.rangeStartMs,
      evalRangeEndMs: INITIAL_BOUNDS.rangeEndMs,
      evalCursorMs: INITIAL_BOUNDS.rangeStartMs,
      evalCursorPinned: false,
      userEditedMarket: false,
      inFlightReset: false,
      pendingScrolls: new Set(),
      layers: [],
      selectedLayerTab: null,
      selectedExpiryDte: null,
      focusedStructureTarget: null,
      loadedTabId: null,
      loadedWorkspaceId: null,
      isSwitchingTab: false,
      editingLayers: false,
      previewLayers: [],
      payoffZoomRange: DEFAULT_PAYOFF_ZOOM_RANGE,
      payoffZoomCenter: null,
      payoffLocked: true,
      payoffHoverSpot: null,
      expiryDateSelectionUnlocked: false,
    }),

  ackTabSwitch: () => set({ isSwitchingTab: false }),

  setCachedLayers: (tabId, layers) =>
    set((s) => {
      const next = new Map(s.layerCache);
      next.set(tabId, layers);
      return { layerCache: next };
    }),

  clearCachedLayers: (tabId) =>
    set((s) => {
      if (!s.layerCache.has(tabId)) return s;
      const next = new Map(s.layerCache);
      next.delete(tabId);
      return { layerCache: next };
    }),
}));

// ── Derived helpers ─────────────────────────────────────────────────────────

export function selectEvalT(s: SimulatorState): number {
  return (s.evalRangeEndMs - s.evalCursorMs) / MS_PER_YEAR;
}

export function selectReady(
  s: SimulatorState,
  _liveSpot: number | null,
  _liveIV: number | null,
): boolean {
  return (
    s.entrySpot !== null &&
    s.entryIV !== null &&
    s.evalSpot !== null &&
    s.scenarioIV !== null
  );
}
